'use client';

import { useState, useEffect } from 'react';
import { usePostHog } from 'posthog-js/react';
import { submitTestimonial } from '@/lib/tracking';
import { useAuth } from './AuthProvider';

interface TestimonialModalProps {
  deviceId: string | null;
  sourceJobId?: string | null;
  onDismiss: () => void;
  onSubmitted: () => void;
}

export function TestimonialModal({ deviceId, sourceJobId, onDismiss, onSubmitted }: TestimonialModalProps) {
  const [rating, setRating] = useState<number>(0);
  const [hoverRating, setHoverRating] = useState<number>(0);
  const [testimonialText, setTestimonialText] = useState('');
  const [email, setEmail] = useState('');
  const [publicPermission, setPublicPermission] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [studentContext, setStudentContext] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const posthog = usePostHog();
  const { user } = useAuth();

  // Pre-fill email if user is authenticated
  useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!testimonialText.trim()) {
      setError('Please share your experience with us!');
      return;
    }

    setIsSubmitting(true);

    try {
      const success = await submitTestimonial(
        deviceId,
        {
          testimonialText,
          rating: rating > 0 ? rating : null,
          email: email || null,
          publicPermission: rating >= 4 && publicPermission,
          displayName,
          studentContext,
          sourceJobId,
          userId: user?.id || null,
        },
      );

      if (success) {
        setSubmitted(true);
        posthog?.capture('testimonial_submitted', {
          rating,
          has_email: !!email,
          text_length: testimonialText.length,
          source_job_id: sourceJobId,
          public_permission: rating >= 4 && publicPermission,
        });
        if (rating >= 4 && publicPermission) {
          posthog?.capture('testimonial_permission_granted', {
            rating,
            source_job_id: sourceJobId,
            has_display_name: Boolean(displayName.trim()),
            has_student_context: Boolean(studentContext.trim()),
          });
        }
        // Keep the success state visible, then let the parent mark the prompt
        // complete and close it without recording a dismissal.
        setTimeout(onSubmitted, 1600);
      } else {
        setError('Failed to submit. Please try again.');
      }
    } catch (err) {
      console.error('Testimonial submission error:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDismiss = () => {
    posthog?.capture('testimonial_modal_dismissed', {
      had_rating: rating > 0,
      had_text: testimonialText.length > 0,
    });
    onDismiss();
  };

  // Success state
  if (submitted) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
        <div className="relative bg-white rounded-2xl shadow-2xl p-8 max-w-md mx-4 text-center animate-in zoom-in-95 fade-in duration-200">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-light flex items-center justify-center">
            <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold mb-2">Thank you! 🙏</h3>
          <p className="text-sm text-muted">Your feedback means the world to us.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleDismiss}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl p-6 sm:p-8 max-w-md mx-4 w-full animate-in zoom-in-95 fade-in duration-200">
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 p-1.5 text-muted hover:text-muted transition-colors rounded-lg hover:bg-muted-bg"
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-primary-light flex items-center justify-center">
            <span className="text-2xl">💬</span>
          </div>
          <h3 className="text-xl font-semibold font-serif mb-1">What did SlideToAnki replace today?</h3>
          <p className="text-sm text-muted">One honest sentence helps us improve the experience.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Star Rating */}
          <div className="text-center">
            <label className="block text-xs font-medium text-muted mb-2">
              How would you rate us?
            </label>
            <div className="flex justify-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="p-1 transition-transform hover:scale-110"
                  aria-label={`Rate ${star} stars`}
                >
                  <svg
                    className={`w-8 h-8 transition-colors ${
                      star <= (hoverRating || rating)
                        ? 'text-yellow-400 fill-yellow-400'
                        : 'text-gray-200 fill-gray-200'
                    }`}
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                </button>
              ))}
            </div>
          </div>

          {/* Testimonial Text */}
          <div>
            <label className="block text-xs font-medium text-muted mb-1.5">
              What would you have done without SlideToAnki? <span className="text-red-400">*</span>
            </label>
            <textarea
              value={testimonialText}
              onChange={(e) => setTestimonialText(e.target.value)}
              placeholder="For example: I would have spent the evening turning this lecture into cards by hand."
              rows={4}
              className="w-full px-3 py-2.5 border border-border rounded-xl text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary/30 resize-none transition-all"
              autoFocus
            />
          </div>

          {rating >= 4 && (
            <div className="space-y-3 rounded-xl border border-primary/20 bg-primary/5 p-4">
              <label className="flex items-start gap-3 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={publicPermission}
                  onChange={event => setPublicPermission(event.target.checked)}
                  className="mt-1 h-4 w-4 accent-[var(--primary)]"
                />
                <span>
                  <strong className="block">SlideToAnki may display this quote publicly</strong>
                  <span className="mt-1 block text-xs leading-5 text-muted">Optional. Nothing is published until it is reviewed, and your email is never displayed.</span>
                </span>
              </label>
              {publicPermission && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <input
                    value={displayName}
                    onChange={event => setDisplayName(event.target.value)}
                    placeholder="Display name (optional)"
                    className="min-h-11 rounded-lg border border-border bg-card px-3 text-sm"
                  />
                  <input
                    value={studentContext}
                    onChange={event => setStudentContext(event.target.value)}
                    placeholder="e.g. MS2 (optional)"
                    className="min-h-11 rounded-lg border border-border bg-card px-3 text-sm"
                  />
                </div>
              )}
            </div>
          )}

          {/* Email (optional) */}
          <div>
            <label className="block text-xs font-medium text-muted mb-1.5">
              Email <span className="text-muted">(optional — for follow-up)</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="w-full px-3 py-2.5 border border-border rounded-xl text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary/30 transition-all"
            />
          </div>

          {/* Error message */}
          {error && (
            <p className="text-sm text-red-500 text-center">{error}</p>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleDismiss}
              className="ui-button ui-button--secondary flex-1"
            >
              Maybe later
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="ui-button ui-button--primary flex-1"
            >
              {isSubmitting ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Sending...
                </>
              ) : (
                'Share feedback'
              )}
            </button>
          </div>
        </form>

        {/* Privacy note */}
        <p className="text-[10px] text-muted text-center mt-4">
          Private by default. Public use requires the explicit permission checkbox above.
        </p>
      </div>
    </div>
  );
}
